import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import FeedbackModal from "../shared/FeedbackModal";

const FULL_TITLE = "Transforming Discontinuity Observations into Predictive Engineering Geological Assessment for Underground Excavations";

const Walkthrough = () => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Splash animation phases: "measuring" -> "centered" -> "settled"
  // The logo starts at the centre of the screen, then moves up into place.
  const [phase, setPhase] = useState("measuring");
  const [centerOffset, setCenterOffset] = useState(0);
  const logoRef = useRef(null);

  // Measure (before first paint) how far the logo's resting spot is from the
  // centre of the screen, so the splash can start with the logo centred.
  useLayoutEffect(() => {
    const rect = logoRef.current.getBoundingClientRect();
    setCenterOffset(window.innerHeight / 2 - (rect.top + rect.height / 2));
    setPhase("centered");
  }, []);

  // Hold the centred logo on screen for a beat, then move it up into place.
  useEffect(() => {
    if (phase !== "centered") return;
    const t = setTimeout(() => setPhase("settled"), 1600);
    return () => clearTimeout(t);
  }, [phase]);

  const restVisible = phase === "settled";

  const openHelp = () => {
    if (typeof window !== "undefined" && typeof window.openXlog3DHelp === "function") {
      window.openXlog3DHelp();
    } else {
      window.dispatchEvent(new Event("xlog3d:open-help"));
    }
  };

  return (
    <div id="page" style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#f0f0f0" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", padding: "15px" }}>
        <div className="container" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div className="row justify-content-center">
            <div className="col-12">
              {/* Main Card */}
              <div style={{
                background: "white",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                padding: "25px 20px",
                textAlign: "center"
              }}>
                {/* Logo (starts centred on the screen, then moves up into place) */}
                <img
                  ref={logoRef}
                  src="/XLog3D Logo_Red.png"
                  alt="Xlog3D Logo"
                  style={{
                    height: "70px",
                    marginBottom: "10px",
                    opacity: phase === "measuring" ? 0 : 1,
                    transform: phase === "settled"
                      ? "translateY(0) scale(1)"
                      : `translateY(${centerOffset}px) scale(2.6)`,
                    transformOrigin: "center center",
                    transition: phase === "settled"
                      ? "transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)"
                      : "none",
                    willChange: "transform"
                  }}
                />

                {/* Title */}
                <h2 style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#333",
                  marginBottom: "8px",
                  opacity: restVisible ? 1 : 0,
                  transition: "opacity 0.6s ease"
                }}>
                  {FULL_TITLE}
                </h2>

                {/* Subtitle + sample log + features (fade in once the logo settles) */}
                <div style={{
                  opacity: restVisible ? 1 : 0,
                  transition: "opacity 0.6s ease"
                }}>
                  {/* Subtitle */}
                  <p style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "18px"
                  }}>
                    A practical tool for engineering geologists and tunnelling professionals.
                  </p>

                  {/* Sample Log */}
                  <img
                    src="/Log_Sample.jpg"
                    alt="Sample discontinuity log with stereonet"
                    style={{
                      width: "70%",
                      maxWidth: "420px",
                      height: "auto",
                      borderRadius: "8px",
                      marginBottom: "18px",
                    }}
                  />

                  {/* Features Card */}
                  <div style={{
                    background: "#f8f9fa",
                    borderRadius: "10px",
                    border: "1px solid #e9ecef",
                    padding: "18px 20px",
                    textAlign: "left"
                  }}>
                    <ul style={{
                      listStyleType: "disc",
                      paddingLeft: "20px",
                      margin: 0,
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: "#444"
                    }}>
                      <li style={{ marginBottom: "10px" }}>
                        <strong>Plot</strong> the 3D traces of discontinuities (joints, bedding, shear planes, foliation, etc.) that intersect a subsurface excavation
                      </li>
                      <li style={{ marginBottom: "10px" }}>
                        <strong>Assess</strong> the influence of discontinuities on stability and support requirements
                      </li>
                      <li style={{ marginBottom: "10px" }}>
                        <strong>Estimate</strong> poor rock class percentage during the investigation stage
                      </li>
                      <li>
                        <strong>Project</strong> discontinuity trace beyond tunnel heading
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons: Proceed, Help, Exit (feedback) */}
        <div style={{ padding: "15px 0", display: "flex", gap: "10px" }}>
          <Link
            to="/disclaimer"
            className="btn btn-m rounded-l bg-green-dark font-900 text-uppercase"
            style={{ flex: 1 }}
          >
            Proceed
          </Link>
          <button
            type="button"
            onClick={openHelp}
            className="btn btn-m rounded-l bg-green-dark font-900 text-uppercase"
            style={{ flex: 1 }}
          >
            User Manual
          </button>
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="btn btn-m rounded-l bg-green-dark font-900 text-uppercase"
            style={{ flex: 1 }}
          >
            Exit
          </button>
        </div>
      </div>
      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        screenLabel="Walkthrough"
        attachGraph={false}
        redirectUrl="https://www.dhawanacademy.com/"
      />
    </div>
  );
};

export default Walkthrough;
